/**
 * Leviathan Web - Statements Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Customer statements management with batch generation
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  RefreshCw,
  Plus,
  FileText,
  Send,
  Eye,
  CheckCircle,
  Clock,
  Search,
  Filter,
  Printer,
  Mail,
  Users,
  X,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface Customer {
  id: string;
  customerNumber: string;
  name: string;
}

interface Statement {
  id: string;
  statementNumber: string;
  customerId: string;
  customer: Customer;
  periodStart: string;
  periodEnd: string;
  statementDate: string;
  dueDate: string | null;
  previousBalance: number;
  totalCharges: number;
  totalCredits: number;
  endingBalance: number;
  current: number;
  over30: number;
  over60: number;
  over90: number;
  status: 'DRAFT' | 'SENT' | 'VIEWED' | 'PAID';
  sentAt: string | null;
  sentVia: string | null;
  emailedTo: string | null;
  viewedAt: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: Statement['status'] }) {
  const config = {
    DRAFT: {
      label: 'Draft',
      icon: <Clock className="w-3 h-3" />,
      className: 'bg-panel-dark border-panel-border text-text-secondary',
    },
    SENT: {
      label: 'Sent',
      icon: <Send className="w-3 h-3" />,
      className: 'bg-steel/20 border-steel text-steel',
    },
    VIEWED: {
      label: 'Viewed',
      icon: <Eye className="w-3 h-3" />,
      className: 'bg-amber/20 border-amber text-amber',
    },
    PAID: {
      label: 'Paid',
      icon: <CheckCircle className="w-3 h-3" />,
      className: 'bg-success-green/20 border-success-green text-success-green',
    },
  };

  const statusConfig = config[status] ?? config.DRAFT;

  return (
    <span
      className={`inline-flex items-center space-x-1.5 px-2 py-1 border text-xs font-mono uppercase ${statusConfig.className}`}
    >
      {statusConfig.icon}
      <span>{statusConfig.label}</span>
    </span>
  );
}

/**
 * Batch Generate Modal
 */
function BatchGenerateModal({
  isOpen,
  onClose,
  onGenerate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (periodStart: string, periodEnd: string) => void;
}) {
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Default to current month
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setPeriodStart(firstDay.toISOString().split('T')[0] || '');
      setPeriodEnd(lastDay.toISOString().split('T')[0] || '');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="relative bg-panel-dark border border-amber w-full max-w-md">
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-panel-border">
          <h2 className="text-lg font-mono text-text-primary uppercase tracking-wider">
            Batch Generate Statements
          </h2>
          <button
            onClick={onClose}
            className="text-text-tertiary hover:text-amber transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-text-secondary font-mono text-sm">
            Generate statements for all customers with outstanding balances for the selected period.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Period Start
              </label>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber px-3 py-2 text-text-primary font-mono focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Period End
              </label>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber px-3 py-2 text-text-primary font-mono focus:outline-none"
              />
            </div>
          </div>

          <div className="bg-background border border-panel-border p-3">
            <div className="flex items-start space-x-2">
              <Users className="w-4 h-4 text-amber flex-shrink-0 mt-0.5" />
              <p className="text-text-tertiary font-mono text-xs">
                Statements will be generated for all active customers with a balance greater than $0.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-4 border-t border-panel-border">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-panel-border text-text-secondary hover:text-text-primary hover:border-text-tertiary transition-colors font-mono text-sm"
          >
            Cancel
          </button>
          <button
            onClick={() => onGenerate(periodStart, periodEnd)}
            disabled={!periodStart || !periodEnd}
            className="flex items-center space-x-2 bg-amber text-background px-4 py-2 font-mono text-sm uppercase tracking-wider hover:bg-amber/80 transition-colors disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            <span>Generate</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Statements Page
 */
const StatementsPage = () => {
  const { accessToken } = useAuth();
  const [statements, setStatements] = useState<Statement[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    fetchStatements();
  }, [statusFilter]);

  const fetchStatements = async (page = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`${API_URL}/api/reports/statements?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStatements(data.statements);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch statements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBatchGenerate = async (periodStart: string, periodEnd: string) => {
    setIsGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/statements/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ periodStart, periodEnd }),
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Generated ${data.generated} statements. ${data.errors} errors.`);
        setShowBatchModal(false);
        fetchStatements();
      }
    } catch (error) {
      console.error('Failed to generate statements:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    try {
      const response = await fetch(`${API_URL}/api/reports/statements/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        fetchStatements(pagination?.page || 1);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const filteredStatements = statements.filter(
    (s) =>
      s.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.customer.customerNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.statementNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/reports"
            className="p-2 text-text-tertiary hover:text-amber transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-text-primary font-mono uppercase tracking-wider">
              Customer Statements
            </h1>
            <p className="text-sm text-text-secondary font-mono mt-1">
              Generate and manage customer account statements
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowBatchModal(true)}
          disabled={isGenerating}
          className="flex items-center space-x-2 bg-amber text-background px-4 py-2 font-mono text-sm uppercase tracking-wider hover:bg-amber/80 transition-colors disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          <span>{isGenerating ? 'Generating...' : 'Batch Generate'}</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
              <input
                type="text"
                placeholder="Search statements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber pl-10 pr-4 py-2 text-text-primary font-mono focus:outline-none"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-text-tertiary" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-background border border-panel-border focus:border-amber px-3 py-2 text-text-primary font-mono focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="SENT">Sent</option>
              <option value="VIEWED">Viewed</option>
              <option value="PAID">Paid</option>
            </select>
          </div>

          {/* Refresh */}
          <button
            onClick={() => fetchStatements()}
            disabled={isLoading}
            className="p-2 border border-panel-border text-text-tertiary hover:text-amber hover:border-amber transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Statements Table */}
      <div className="relative">
        <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border overflow-x-auto">
          {isLoading ? (
            <div className="text-center py-12 text-text-tertiary font-mono">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading statements...
            </div>
          ) : filteredStatements.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-text-tertiary mx-auto mb-3" />
              <div className="text-text-tertiary font-mono">No statements found</div>
              <p className="text-text-tertiary/60 font-mono text-sm mt-1">
                Generate statements using the batch generate feature
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-background border-b border-panel-border">
                <tr>
                  <th className="text-left px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Statement #
                  </th>
                  <th className="text-left px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Customer
                  </th>
                  <th className="text-left px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Period
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Balance
                  </th>
                  <th className="text-center px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredStatements.map((statement) => (
                  <tr
                    key={statement.id}
                    className="border-b border-panel-border/50 hover:bg-amber/5"
                  >
                    <td className="px-4 py-3">
                      <span className="text-amber font-mono font-semibold">
                        {statement.statementNumber}
                      </span>
                      <div className="text-text-tertiary text-xs font-mono">
                        {formatDate(statement.statementDate)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/customers/${statement.customerId}`}
                        className="text-text-primary hover:text-amber font-mono"
                      >
                        {statement.customer.name}
                      </Link>
                      <div className="text-text-tertiary text-xs font-mono">
                        {statement.customer.customerNumber}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-text-secondary text-sm">
                      {formatDate(statement.periodStart)} - {formatDate(statement.periodEnd)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-amber font-mono font-bold">
                        {formatCurrency(statement.endingBalance)}
                      </span>
                      {statement.over90 > 0 && (
                        <div className="text-error-red text-xs font-mono">
                          {formatCurrency(statement.over90)} 90+ days
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={statement.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/reports/statements/${statement.id}`}
                          className="p-1.5 text-text-tertiary hover:text-amber transition-colors"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => window.print()}
                          className="p-1.5 text-text-tertiary hover:text-amber transition-colors"
                          title="Print"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        {statement.status === 'DRAFT' && (
                          <button
                            onClick={() => handleUpdateStatus(statement.id, 'SENT')}
                            className="p-1.5 text-text-tertiary hover:text-steel transition-colors"
                            title="Mark as Sent"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        )}
                        {statement.status === 'SENT' && (
                          <button
                            onClick={() => handleUpdateStatus(statement.id, 'PAID')}
                            className="p-1.5 text-text-tertiary hover:text-success-green transition-colors"
                            title="Mark as Paid"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-text-tertiary font-mono text-sm">
            Showing {(pagination.page - 1) * pagination.limit + 1} -{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => fetchStatements(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 border border-panel-border text-text-secondary hover:text-amber hover:border-amber disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm"
            >
              Previous
            </button>
            <span className="text-text-tertiary font-mono text-sm">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => fetchStatements(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="px-3 py-1 border border-panel-border text-text-secondary hover:text-amber hover:border-amber disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Batch Generate Modal */}
      <BatchGenerateModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        onGenerate={handleBatchGenerate}
      />
    </div>
  );
};

export default StatementsPage;
